import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import axiosInstance from './axiosInstance';

const extractFilename = (contentDisposition = '') => {
  const match = contentDisposition.match(/filename\*?=(?:UTF-8''|"?)([^";]+)/i);
  if (match && match[1]) {
    try {
      return decodeURIComponent(match[1].replace(/"/g, '').trim());
    } catch (err) {
      return match[1].replace(/"/g, '').trim();
    }
  }
  return '';
};

const triggerDownload = (blobData, filename) => {
  const blob = new Blob([blobData], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const defaultFilename = () => `finance-report-${Date.now()}.xlsx`;

const DEFAULT_REPORT_TYPES = [
  { report_type_id: 'monthly_spend_breakdown', name: 'Monthly Spend Breakdown' },
  { report_type_id: 'monthly_spend_breakdown_summary', name: 'Monthly Spend Breakdown - Summary' },
  {
    report_type_id: 'monthly_spend_and_spend_on_sales_breakdown',
    name: 'Monthly Spend And Spend On Sales Breakdown',
  },
  {
    report_type_id: 'monthly_spend_and_spend_on_sales_breakdown_summary',
    name: 'Monthly Spend And Spend On Sales Breakdown - Summary',
  },
  { report_type_id: 'spend_on_sales_breakdown', name: 'Spend On Sales Breakdown' },
  { report_type_id: 'spend_on_sales_breakdown_detailed', name: 'Spend On Sales Breakdown - Detailed' },
];

const normaliseEmailList = (emails = []) =>
  [...new Set((Array.isArray(emails) ? emails : []).map((email) => String(email).trim().toLowerCase()).filter(Boolean))];

const normaliseNotifySettings = (settings = {}) => ({
  send_email: Boolean(settings.send_email),
  notify_users: normaliseEmailList(settings.notify_users),
  full_report: Boolean(settings.full_report),
});

const normaliseReportSettingsMap = (settingsMap = {}, reportTypes = DEFAULT_REPORT_TYPES) => {
  const normalised = {};

  reportTypes.forEach((reportType) => {
    normalised[reportType.report_type_id] = normaliseNotifySettings(settingsMap?.[reportType.report_type_id]);
  });

  Object.entries(settingsMap || {}).forEach(([reportTypeId, settings]) => {
    if (!normalised[reportTypeId]) {
      normalised[reportTypeId] = normaliseNotifySettings(settings);
    }
  });

  return normalised;
};

const getNotifySettingsForReportType = (settingsMap, reportTypeId) =>
  normaliseNotifySettings(settingsMap?.[reportTypeId]);

const parseErrorMessage = async (error) => {
  const fallback = 'Failed to generate the report.';
  if (!error?.response) {
    return error?.message || fallback;
  }

  const { data } = error.response;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      try {
        const parsed = JSON.parse(text);
        if (parsed?.error) return parsed.error;
        if (parsed?.message) return parsed.message;
        if (Array.isArray(parsed?.errors) && parsed.errors.length) {
          const first = parsed.errors[0];
          return first?.error || first?.message || fallback;
        }
      } catch (parseErr) {
        return text || fallback;
      }
    } catch (blobErr) {
      return fallback;
    }
  }

  return error.response?.data?.error || error.response?.data?.message || fallback;
};

const ReportsPage = () => {
  const [isGenerating, setIsGenerating] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [newNotifyEmail, setNewNotifyEmail] = useState('');
  const [notifyInputError, setNotifyInputError] = useState('');
  const [savedNotifySettingsByReportType, setSavedNotifySettingsByReportType] = useState({});
  const [draftNotifySettingsByReportType, setDraftNotifySettingsByReportType] = useState({});
  const [loadingNotifySettings, setLoadingNotifySettings] = useState(true);
  const [notifySettingsError, setNotifySettingsError] = useState('');
  const [savingNotifySettings, setSavingNotifySettings] = useState(false);
  const [reportTypes, setReportTypes] = useState(DEFAULT_REPORT_TYPES);
  const [reportType, setReportType] = useState(DEFAULT_REPORT_TYPES[0].report_type_id);
  const [loadingReportTypes, setLoadingReportTypes] = useState(true);
  const [reportTypeError, setReportTypeError] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [selectedMonthKey, setSelectedMonthKey] = useState('');

  const selectedReportLabel = useMemo(
    () => reportTypes.find((type) => type.report_type_id === reportType)?.name || 'Selected Report',
    [reportType, reportTypes],
  );

  const selectedNotifySettings = useMemo(
    () => getNotifySettingsForReportType(draftNotifySettingsByReportType, reportType),
    [draftNotifySettingsByReportType, reportType],
  );

  const initialNotifySettings = useMemo(
    () => getNotifySettingsForReportType(savedNotifySettingsByReportType, reportType),
    [savedNotifySettingsByReportType, reportType],
  );

  const monthOptions = useMemo(() => {
    const options = [];
    const now = new Date();
    const anchor = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1));
    for (let i = 0; i < 12; i += 1) {
      const current = new Date(Date.UTC(anchor.getUTCFullYear(), anchor.getUTCMonth() - i, 1));
      const monthLabel = current.toLocaleString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
      const monthKey = `${current.getUTCFullYear()}-${String(current.getUTCMonth() + 1).padStart(2, '0')}`;
      const start = `${monthKey}-01`;
      const endDateValue = new Date(Date.UTC(current.getUTCFullYear(), current.getUTCMonth() + 1, 0));
      const end = `${monthKey}-${String(endDateValue.getUTCDate()).padStart(2, '0')}`;
      options.push({ monthKey, label: monthLabel, start, end });
    }
    options.push({ monthKey: 'custom', label: 'Custom', start: '', end: '' });
    return options;
  }, []);

  const updateSelectedNotifySettings = (updater) => {
    setDraftNotifySettingsByReportType((prev) => {
      const current = getNotifySettingsForReportType(prev, reportType);
      const nextSettings = typeof updater === 'function' ? updater(current) : updater;
      return {
        ...prev,
        [reportType]: normaliseNotifySettings(nextSettings),
      };
    });
  };

  useEffect(() => {
    if (!monthOptions.length) return;
    const currentMonth = monthOptions.find((option) => option.monthKey !== 'custom');
    if (!currentMonth) return;
    setSelectedMonthKey(currentMonth.monthKey);
    setStartDate(currentMonth.start);
    const today = new Date();
    const todayIso = today.toISOString().slice(0, 10);
    setEndDate(todayIso);
  }, [monthOptions]);

  useEffect(() => {
    if (!selectedMonthKey) return;
    if (selectedMonthKey === 'custom') {
      return;
    }
    const selected = monthOptions.find((option) => option.monthKey === selectedMonthKey);
    if (!selected) return;
    setStartDate(selected.start);
    const now = new Date();
    const currentKey = `${now.getUTCFullYear()}-${String(now.getUTCMonth() + 1).padStart(2, '0')}`;
    if (selected.monthKey === currentKey) {
      setEndDate(new Date().toISOString().slice(0, 10));
    } else {
      setEndDate(selected.end);
    }
  }, [selectedMonthKey, monthOptions]);

  useEffect(() => {
    const fetchReportTypes = async () => {
      try {
        setLoadingReportTypes(true);
        const response = await axiosInstance.get('/api/v1/finance/report/type');
        const serverTypes = Array.isArray(response?.data?.data) ? response.data.data : [];
        if (serverTypes.length) {
          setReportTypes(serverTypes);
          setReportType((current) => {
            if (serverTypes.some((type) => type.report_type_id === current)) {
              return current;
            }
            return serverTypes[0].report_type_id;
          });
          setReportTypeError('');
        } else {
          setReportTypes(DEFAULT_REPORT_TYPES);
          setReportType(DEFAULT_REPORT_TYPES[0].report_type_id);
        }
      } catch (err) {
        console.error('Failed to load finance report types:', err);
        setReportTypes(DEFAULT_REPORT_TYPES);
        setReportType(DEFAULT_REPORT_TYPES[0].report_type_id);
        setReportTypeError('Unable to load report types. Using defaults.');
      } finally {
        setLoadingReportTypes(false);
      }
    };

    fetchReportTypes();
  }, []);

  useEffect(() => {
    const fetchNotifySettings = async () => {
      try {
        setLoadingNotifySettings(true);
        const response = await axiosInstance.get('/api/v1/finance/notify');
        const settingsMap = normaliseReportSettingsMap(response?.data?.data?.report_settings, DEFAULT_REPORT_TYPES);
        setSavedNotifySettingsByReportType(settingsMap);
        setDraftNotifySettingsByReportType(settingsMap);
        setNotifySettingsError('');
      } catch (err) {
        console.error('Failed to load finance notification settings:', err);
        const emptySettingsMap = normaliseReportSettingsMap({}, DEFAULT_REPORT_TYPES);
        setSavedNotifySettingsByReportType(emptySettingsMap);
        setDraftNotifySettingsByReportType(emptySettingsMap);
        setNotifySettingsError('Unable to load notification preferences. You can still generate reports.');
      } finally {
        setLoadingNotifySettings(false);
      }
    };

    fetchNotifySettings();
  }, []);

  useEffect(() => {
    setSavedNotifySettingsByReportType((prev) => normaliseReportSettingsMap(prev, reportTypes));
    setDraftNotifySettingsByReportType((prev) => normaliseReportSettingsMap(prev, reportTypes));
  }, [reportTypes]);

  useEffect(() => {
    setNewNotifyEmail('');
    setNotifyInputError('');
  }, [reportType]);

  const notifySettingsDirty = useMemo(() => {
    if (selectedNotifySettings.send_email !== initialNotifySettings.send_email) {
      return true;
    }
    if (selectedNotifySettings.full_report !== initialNotifySettings.full_report) {
      return true;
    }

    const draftUsers = [...selectedNotifySettings.notify_users].sort().join(',');
    const savedUsers = [...initialNotifySettings.notify_users].sort().join(',');
    return draftUsers !== savedUsers;
  }, [initialNotifySettings, selectedNotifySettings]);

  const handleAddNotifyEmail = () => {
    const trimmed = newNotifyEmail.trim().toLowerCase();
    if (!trimmed) {
      setNotifyInputError('Enter an email address.');
      return;
    }
    if (!trimmed.includes('@')) {
      setNotifyInputError('Enter a valid email address.');
      return;
    }
    if (selectedNotifySettings.notify_users.includes(trimmed)) {
      setNotifyInputError('This email is already added.');
      return;
    }
    updateSelectedNotifySettings((current) => ({
      ...current,
      notify_users: [...current.notify_users, trimmed],
    }));
    setNewNotifyEmail('');
    setNotifyInputError('');
  };

  const handleRemoveNotifyEmail = (email) => {
    updateSelectedNotifySettings((current) => ({
      ...current,
      notify_users: current.notify_users.filter((item) => item !== email),
    }));
  };

  const handleSaveNotifySettings = async () => {
    setSavingNotifySettings(true);
    try {
      const payload = {
        report_type: reportType,
        send_email: selectedNotifySettings.send_email,
        notify_users: selectedNotifySettings.notify_users,
        full_report: selectedNotifySettings.full_report,
      };
      const response = await axiosInstance.put('/api/v1/finance/notify', payload);
      const savedSettingsMap = normaliseReportSettingsMap(response?.data?.data?.report_settings, reportTypes);
      setSavedNotifySettingsByReportType(savedSettingsMap);
      setDraftNotifySettingsByReportType(savedSettingsMap);
      setSuccessMessage(`Notification settings updated for ${selectedReportLabel}.`);
      setErrorMessage('');
    } catch (err) {
      console.error('Failed to save notification settings:', err);
      const message = err?.response?.data?.error || 'Unable to save notification settings.';
      setErrorMessage(message);
    } finally {
      setSavingNotifySettings(false);
    }
  };

  const handleGenerateReport = async () => {
    if (!startDate || !endDate) {
      setErrorMessage('Please select both a start date and end date.');
      return;
    }

    setIsGenerating(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const params = new URLSearchParams({
        start_date: startDate,
        end_date: endDate,
      });

      const payload = {
        send_email: selectedNotifySettings.send_email,
        notify_users: selectedNotifySettings.notify_users,
        full_report: selectedNotifySettings.full_report,
        report_type: reportType,
      };

      const response = await axiosInstance.post(`/api/v1/finance?${params.toString()}`, payload, {
        responseType: 'blob',
      });

      const disposition = response.headers?.['content-disposition'] || '';
      const filename = extractFilename(disposition) || defaultFilename();
      triggerDownload(response.data, filename);
      setSuccessMessage('Report generated successfully. Your download should begin shortly.');
    } catch (error) {
      const message = await parseErrorMessage(error);
      setErrorMessage(message);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <Box
      sx={{
        maxWidth: 1100,
        mx: 'auto',
        px: '5%',
        py: 4,
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-start',
      }}
    >
      <Typography variant="h4" gutterBottom sx={{ textAlign: 'left', width: '100%' }}>
        Reports
      </Typography>

      <Box sx={{ width: '100%', mb: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ textAlign: 'left' }}>
          Generate Reports
        </Typography>
        <Typography variant="body1" color="textSecondary" sx={{ mb: 2, textAlign: 'left' }}>
          Generate reports from the uploaded finance data.
        </Typography>
      </Box>

      <Stack spacing={2} sx={{ width: '100%', mb: 4 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <FormControl fullWidth>
            <InputLabel id="report-month-label">Report Month</InputLabel>
            <Select
              labelId="report-month-label"
              value={selectedMonthKey}
              label="Report Month"
              onChange={(event) => setSelectedMonthKey(event.target.value)}
            >
              {monthOptions.map((option) => (
                <MenuItem key={option.monthKey} value={option.monthKey}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Start Date"
            type="date"
            value={startDate}
            onChange={(event) => {
              setStartDate(event.target.value);
              setSelectedMonthKey('custom');
            }}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
          <TextField
            label="End Date"
            type="date"
            value={endDate}
            onChange={(event) => {
              setEndDate(event.target.value);
              setSelectedMonthKey('custom');
            }}
            InputLabelProps={{ shrink: true }}
            fullWidth
          />
        </Stack>

        <FormControl fullWidth disabled={loadingReportTypes}>
          <InputLabel id="report-type-label">Report Type</InputLabel>
          <Select
            labelId="report-type-label"
            value={reportType}
            label="Report Type"
            onChange={(event) => setReportType(event.target.value)}
          >
            {reportTypes.map((type) => (
              <MenuItem key={type.report_type_id} value={type.report_type_id}>
                {type.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        {reportTypeError && <Alert severity="warning">{reportTypeError}</Alert>}
      </Stack>

      <Box sx={{ width: '100%', mb: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ textAlign: 'left', display: 'flex', alignItems: 'center', gap: 1 }}>
          Notification Settings
          {loadingNotifySettings && <CircularProgress size={16} />}
        </Typography>
        <Typography variant="body2" color="textSecondary" sx={{ mb: 2, textAlign: 'left' }}>
          Configure who gets emailed for {selectedReportLabel} and whether that email should include the full report.
        </Typography>
        <Stack spacing={2}>
          {notifySettingsError && <Alert severity="warning">{notifySettingsError}</Alert>}
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedNotifySettings.send_email}
                  onChange={(event) =>
                    updateSelectedNotifySettings((current) => ({
                      ...current,
                      send_email: event.target.checked,
                    }))
                  }
                  disabled={loadingNotifySettings}
                />
              }
              label="Email Report"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedNotifySettings.full_report}
                  onChange={(event) =>
                    updateSelectedNotifySettings((current) => ({
                      ...current,
                      full_report: event.target.checked,
                    }))
                  }
                  disabled={loadingNotifySettings}
                />
              }
              label="Full Report"
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="flex-start">
            <TextField
              label="Add notify user"
              value={newNotifyEmail}
              onChange={(event) => setNewNotifyEmail(event.target.value)}
              error={Boolean(notifyInputError)}
              helperText={notifyInputError}
              fullWidth
              disabled={loadingNotifySettings}
            />
            <Button
              variant="outlined"
              onClick={handleAddNotifyEmail}
              sx={{ height: 56 }}
              disabled={loadingNotifySettings || !newNotifyEmail.trim()}
            >
              Add
            </Button>
          </Stack>
          <Stack direction="row" flexWrap="wrap" sx={{ gap: 1 }}>
            {selectedNotifySettings.notify_users.map((email) => (
              <Chip
                key={`${reportType}-${email}`}
                label={email}
                onDelete={() => handleRemoveNotifyEmail(email)}
                sx={{ mb: 1 }}
                disabled={loadingNotifySettings}
              />
            ))}
          </Stack>
          <Button
            variant="contained"
            onClick={handleSaveNotifySettings}
            disabled={loadingNotifySettings || savingNotifySettings || !notifySettingsDirty}
          >
            {savingNotifySettings ? <CircularProgress size={20} color="inherit" /> : 'Save Notification Settings'}
          </Button>
        </Stack>
      </Box>

      <Box sx={{ mt: 1, mb: 2, display: 'flex', gap: 2 }}>
        <Button
          variant="contained"
          color="primary"
          onClick={handleGenerateReport}
          disabled={isGenerating}
        >
          {isGenerating ? <CircularProgress size={24} color="inherit" /> : 'Generate Report'}
        </Button>
      </Box>

      {successMessage && (
        <Box sx={{ mt: 2, width: '100%' }}>
          <Alert severity="success">{successMessage}</Alert>
        </Box>
      )}

      {errorMessage && (
        <Box sx={{ mt: 2, width: '100%' }}>
          <Alert severity="error">{errorMessage}</Alert>
        </Box>
      )}
    </Box>
  );
};

export default ReportsPage;
